import { useCallback, useState } from 'react';
import { message } from 'antd';
import {
  createSavedFilterSet,
  updateSavedFilterSet,
  deleteSavedFilterSet,
} from '@/apis/shipmentTabs';
import {
  removeTab,
  setActiveTabId,
  updateTabFilterSet,
} from '@/redux-toolkit/slice/shipmentTabSlice';
import type { AppDispatch } from '@/redux-toolkit/store';
import type {
  AppliedFilter,
  FilterArrayItem,
  FilterSetVisibility,
  SavedFilterSet,
  ShipmentLegType,
  ShipmentTab,
} from '../types';

interface SavePayload {
  name: string;
  visibility: FilterSetVisibility;
  filters: FilterArrayItem[];
  appliedFilters: AppliedFilter[];
  teamIds: string[];
  pin_to_tab?: boolean;
}

interface UseShipmentSavedFilterSetModalParams {
  token: string;
  dispatch: AppDispatch;
  activeTabId: string;
  activeLegType?: ShipmentLegType;
  onTabChange: (tabId: string, tabOverride?: ShipmentTab) => void;
}

type DeleteTarget = {
  filterSet: Pick<SavedFilterSet, 'id'>;
  tabId?: string | null;
};

export const useShipmentSavedFilterSetModal = ({
  token,
  dispatch,
  activeTabId,
  activeLegType = 'CL',
  onTabChange,
}: UseShipmentSavedFilterSetModalParams) => {
  const [savedFilterModalOpen, setSavedFilterModalOpen] = useState(false);
  const [editingFilterSet, setEditingFilterSet] =
    useState<SavedFilterSet | null>(null);
  const [editingTabId, setEditingTabId] = useState<string | null>(null);

  const closeModal = useCallback(() => {
    setSavedFilterModalOpen(false);
    setEditingFilterSet(null);
    setEditingTabId(null);
  }, []);

  const openCreateModal = useCallback(() => {
    setEditingFilterSet(null);
    setEditingTabId(null);
    setSavedFilterModalOpen(true);
  }, []);

  const openEditModal = useCallback((item: ShipmentTab | SavedFilterSet) => {
    if ('savedFilterSet' in item) {
      setEditingFilterSet(item.savedFilterSet);
      setEditingTabId(item.id);
    } else {
      setEditingFilterSet(item);
      setEditingTabId(null);
    }

    setSavedFilterModalOpen(true);
  }, []);

  const handleSave = useCallback(
    async (data: SavePayload) => {
      try {
        if (editingFilterSet) {
          const updated = await updateSavedFilterSet(
            token,
            editingFilterSet.id,
            { ...data, legType: activeLegType }
          );

          if (editingTabId) {
            dispatch(
              updateTabFilterSet({
                tabId: editingTabId,
                savedFilterSet: updated,
              })
            );

            if (activeTabId === editingTabId) {
              const updatedTab: ShipmentTab = {
                id: editingTabId,
                savedFilterSet: updated,
              } as ShipmentTab;

              onTabChange(editingTabId, updatedTab);
            }
          }

          message.success('Filter set updated successfully.');
          closeModal();
          return;
        }

        await createSavedFilterSet(token, {
          ...data,
          legType: activeLegType,
          pin_to_tab: false,
        });

        message.success('Filter set created successfully.');
        closeModal();
      } catch {
        message.error('Failed to save filter set. Please try again.');
        throw new Error('Failed to save filter set');
      }
    },
    [
      activeTabId,
      activeLegType,
      closeModal,
      dispatch,
      editingFilterSet,
      editingTabId,
      onTabChange,
      token,
    ]
  );

  const handleDelete = useCallback(
    async (target?: DeleteTarget) => {
      const targetFilterSet = target?.filterSet ?? editingFilterSet;
      const targetTabId =
        target?.tabId !== undefined ? target.tabId : editingTabId;

      if (!targetFilterSet?.id) return;

      try {
        await deleteSavedFilterSet(token, targetFilterSet.id);

        if (targetTabId) {
          dispatch(removeTab(targetTabId));

          const nextTabId =
            activeTabId === targetTabId ? 'all_shipments' : activeTabId;

          dispatch(setActiveTabId(nextTabId));
          onTabChange(nextTabId);
        }

        if (!target) {
          closeModal();
        }
      } catch {
        message.error('Failed to delete filter set. Please try again.');
        throw new Error('Failed to delete filter set');
      }
    },
    [
      activeTabId,
      closeModal,
      dispatch,
      editingFilterSet,
      editingTabId,
      onTabChange,
      token,
    ]
  );

  return {
    savedFilterModalOpen,
    editingFilterSet,
    editingTabId,
    openCreateModal,
    openEditModal,
    closeModal,
    handleSave,
    handleDelete,
  };
};
